import { ReactNode } from 'react';

type BadgeColor = 'default' | 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'gray';

// Neben den Farbnamen sind semantische Aliase und Lead-Status erlaubt —
// etliche Aufrufer übergeben den Status direkt als variant.
type BadgeVariant =
  | BadgeColor
  | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
  | 'new' | 'contacted' | 'in_progress' | 'converted' | 'archived';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  dot?: boolean;
  className?: string;
}

// Farben der Status-Aliase entsprechen statusColors in lib/types.ts
const colorAliases: Record<string, BadgeColor> = {
  primary: 'blue',
  secondary: 'gray',
  success: 'green',
  warning: 'yellow',
  danger: 'red',
  new: 'blue',
  contacted: 'yellow',
  in_progress: 'purple',
  converted: 'green',
  archived: 'gray',
};

export default function Badge({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  className = ''
}: BadgeProps) {
  const color: BadgeColor = colorAliases[variant] ?? (variant as BadgeColor);

  const variants: Record<BadgeColor, string> = {
    default: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    green: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    yellow: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    gray: 'bg-zinc-800 text-zinc-400 border-zinc-700',
  };

  const sizes = {
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2 py-0.5 text-xs',
  };

  const dotColors: Record<BadgeColor, string> = {
    default: 'bg-zinc-400',
    blue: 'bg-blue-400',
    green: 'bg-emerald-400',
    yellow: 'bg-amber-400',
    red: 'bg-red-400',
    purple: 'bg-purple-400',
    gray: 'bg-zinc-400',
  };

  return (
    <span className={`
      inline-flex items-center gap-1.5 font-medium rounded-md border
      ${variants[color]} ${sizes[size]} ${className}
    `}>
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full ${dotColors[color]}`} />
      )}
      {children}
    </span>
  );
}
